import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { getAsset } from '../../../../util/assets';
import Badge from '../../Badge';
import PriceDisplay from '../../PriceDisplay';
import CheckoutItemQuantityInput from '../../../../services/checkout/components/CheckoutCartItem/CheckoutItemQuantityInput';
import Button from '../../Button';
import AlbumCustomPrintPreviewModal from '../../../../apps/pro/views/HierarchyItem/Matches/components/PurchasingOptions/components/AlbumCustomPrintOption/AlbumCustomPrintPreviewModal';
import ImagePreload from '../../ImagePreload';
import { formatPriceForDisplay } from '../../../../util/format-money';
import { getAlbumCustomPrintPrice } from '../../../../util/custom-prints';

const propTypes = {
  className: PropTypes.string,
  albumCustomPrint: PropTypes.shape({
    id: PropTypes.string.isRequired,
    custom_print: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
  }).isRequired,
  subscriptionAvailable: PropTypes.bool,
  hasSubscriptionInCart: PropTypes.bool,
  cartItem: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  onAlbumCustomPrintToggle: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  identity: PropTypes.shape({
    id: PropTypes.string,
  }),
  albumMembershipGroup: PropTypes.shape({
    id: PropTypes.string,
  }),
};

const AlbumCustomPrintOption = ({
  className,
  albumCustomPrint,
  subscriptionAvailable,
  hasSubscriptionInCart,
  cartItem,
  onAlbumCustomPrintToggle,
  onUpdate,
}) => {
  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  const classes = classNames({
    [className]: !!className,
    'purchasing-option-item': true,
    'purchasing-option-item-album-custom-print': true,
    'is-selected': !!cartItem,
  });

  const { custom_print: customPrint } = albumCustomPrint;
  const { name, description, preview_image: previewImage } = customPrint;
  const hasPreviewImage = !!previewImage;
  const price = getAlbumCustomPrintPrice(albumCustomPrint);
  const discountPrice = getAlbumCustomPrintPrice(albumCustomPrint, true);
  const shouldDiscount = hasSubscriptionInCart;

  return (
    <div className={classes}>
      <div className="purchasing-option-item-thumb">
        <img
          width={75}
          src={previewImage?.url ?? getAsset('/plaque.svg')}
          alt=""
        />
      </div>

      <div className="purchasing-option-item-content-wrap">
        <span className="purchasing-option-item-title">{name}</span>
        <div className="purchasing-option-item-info-extra">
          <Badge badgeStyle="yellow" className="text-extra-bold">
            <PriceDisplay
              price={shouldDiscount && !!discountPrice ? discountPrice : price}
              oldPrice={price}
              showDiscount={shouldDiscount}
              currency={albumCustomPrint.price.currency}
            />
          </Badge>
        </div>

        <div className="purchasing-option-item-content">
          <p className="text-sm lh-normal text-dark text-bold">
            {description ?? ''}
          </p>

          {hasPreviewImage && (
            <p className="mb-0">
              <Button
                btnStyle="link"
                className="text-sm lh-normal text-extra-bold"
                onClick={() => setPreviewModalOpen(true)}
              >
                See sample
              </Button>
            </p>
          )}

          {discountPrice > 0 && subscriptionAvailable && (
            <p className="d-block text-sm text-italic text-bold">
              Only{' '}
              <span className="text-primary">
                ${formatPriceForDisplay(discountPrice, true)}
              </span>{' '}
              if purchased with a Digital Photo Pass. Discount is limited to 1
              print.
            </p>
          )}
        </div>

        <div className="purchasing-option-item-actions">
          <CheckoutItemQuantityInput
            min={0}
            onChange={value => {
              if ((!cartItem && value > 0) || (!!cartItem && value === 0)) {
                // Either no cart item in cart or removing cart item.
                onAlbumCustomPrintToggle();
                return;
              }

              // Else, update the quantity.
              onUpdate(cartItem.id, {
                quantity: value,
              });
            }}
            value={cartItem?.quantity || 0}
            label=""
          />
        </div>
      </div>

      {hasPreviewImage && (
        <Fragment>
          <AlbumCustomPrintPreviewModal
            forceOpen={previewModalOpen}
            onClose={() => setPreviewModalOpen(false)}
            albumCustomPrint={albumCustomPrint}
            onAlbumCustomPrintToggle={onAlbumCustomPrintToggle}
            shouldDiscount={shouldDiscount}
            albumCustomPrintInCart={!!cartItem}
            title="Preview this print!"
            disableSelection
          />
          <ImagePreload urls={[previewImage.url]} />
        </Fragment>
      )}
    </div>
  );
};

AlbumCustomPrintOption.propTypes = propTypes;

export default AlbumCustomPrintOption;
